/**
 * レシピ管理システム
 * メニューと材料の紐付け、原価計算、在庫確認
 */

import React, { useState, useEffect } from 'react';
import {
  ChefHat, Package, Calculator, AlertTriangle, Plus, Trash2,
  Save, Edit, DollarSign, TrendingUp, TrendingDown, Check, X
} from 'lucide-react';
import apiClient from '../../utils/apiClient';
import PageContainer from '../layout/PageContainer';

interface Menu {
  id: string;
  name: string;
  category: string;
  price: number;
  active: number;
  ingredient_count: number;
  total_cost: number;
  profit_margin: number;
}

interface RecipeIngredient {
  recipe_id?: number;
  ingredient_id: number;
  ingredient_name: string;
  ingredient_category: string;
  quantity: number;
  unit: string;
  cost_per_unit: number;
  base_unit: string;
  ingredient_cost: number;
  current_stock: number;
  stock_status: 'OK' | 'LOW' | 'SHORTAGE';
}

interface RecipeDetail {
  menu: Menu;
  ingredients: RecipeIngredient[];
  summary: {
    totalCost: number;
    sellingPrice: number;
    profit: number;
    profitMargin: number;
    canMake: boolean;
  };
}

interface AvailableIngredient {
  id: number;
  name: string;
  category: string;
  base_unit: string;
  cost_per_unit: number;
  current_stock: number;
}

const RecipeManagement: React.FC = () => {
  const [menus, setMenus] = useState<Menu[]>([]);
  const [selectedMenu, setSelectedMenu] = useState<Menu | null>(null);
  const [recipeDetail, setRecipeDetail] = useState<RecipeDetail | null>(null);
  const [availableIngredients, setAvailableIngredients] = useState<AvailableIngredient[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<RecipeIngredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'list' | 'cost' | 'possible'>('list');
  const [costAnalysis, setCostAnalysis] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [menusRes, ingredientsRes, costRes] = await Promise.all([
        apiClient.get('/recipe-management/menus'),
        apiClient.get('/inventory-integration/ingredients'),
        apiClient.get('/recipe-management/cost-analysis')
      ]);

      setMenus(menusRes.data);
      setAvailableIngredients(ingredientsRes.data);
      setCostAnalysis(costRes.data);
    } catch (error) {
      console.error('データ取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipeDetail = async (menuId: string) => {
    try {
      const res = await apiClient.get(`/recipe-management/menus/${menuId}/recipe`);
      setRecipeDetail(res.data);
      setEditingRecipe(res.data.ingredients);
    } catch (error) {
      console.error('レシピ詳細取得エラー:', error);
    }
  };

  const handleMenuSelect = (menu: Menu) => {
    setSelectedMenu(menu);
    fetchRecipeDetail(menu.id);
    setEditMode(false);
  };

  const handleAddIngredient = () => {
    if (availableIngredients.length > 0) {
      const newIngredient: RecipeIngredient = {
        ingredient_id: availableIngredients[0].id,
        ingredient_name: availableIngredients[0].name,
        ingredient_category: availableIngredients[0].category,
        quantity: 0.1,
        unit: availableIngredients[0].base_unit,
        cost_per_unit: availableIngredients[0].cost_per_unit,
        base_unit: availableIngredients[0].base_unit,
        ingredient_cost: 0.1 * availableIngredients[0].cost_per_unit,
        current_stock: availableIngredients[0].current_stock,
        stock_status: 'OK'
      };
      setEditingRecipe([...editingRecipe, newIngredient]);
    }
  };

  const handleRemoveIngredient = (index: number) => {
    setEditingRecipe(editingRecipe.filter((_, i) => i !== index));
  };

  const handleIngredientChange = (index: number, field: string, value: any) => {
    const updated = [...editingRecipe];

    if (field === 'ingredient_id') {
      const ingredient = availableIngredients.find(i => i.id === parseInt(value));
      if (ingredient) {
        updated[index] = {
          ...updated[index],
          ingredient_id: ingredient.id,
          ingredient_name: ingredient.name,
          ingredient_category: ingredient.category,
          unit: ingredient.base_unit,
          base_unit: ingredient.base_unit,
          cost_per_unit: ingredient.cost_per_unit,
          current_stock: ingredient.current_stock,
          ingredient_cost: updated[index].quantity * ingredient.cost_per_unit
        };
      }
    } else if (field === 'quantity') {
      updated[index].quantity = parseFloat(value) || 0;
      updated[index].ingredient_cost = updated[index].quantity * (updated[index].cost_per_unit || 0);
    } else if (field === 'unit') {
      updated[index].unit = value;
    } else if (field === 'cost_per_unit') {
      updated[index].cost_per_unit = parseFloat(value) || 0;
      updated[index].ingredient_cost = updated[index].quantity * updated[index].cost_per_unit;
    }

    setEditingRecipe(updated);
  };

  const handleSaveRecipe = async () => {
    if (!selectedMenu) return;

    try {
      const ingredients = editingRecipe.map(ing => ({
        ingredientId: ing.ingredient_id,
        quantity: ing.quantity,
        unit: ing.unit,
        costPerUnit: ing.cost_per_unit  // 材料単価も送信
      }));

      await apiClient.put(`/recipe-management/menus/${selectedMenu.id}/recipe`, {
        ingredients
      });

      alert('レシピを保存しました');
      setEditMode(false);
      fetchRecipeDetail(selectedMenu.id);
      fetchData(); // メニュー一覧も更新
    } catch (error) {
      console.error('レシピ保存エラー:', error);
      alert('レシピの保存に失敗しました');
    }
  };

  const getProfitMarginColor = (margin: number) => {
    if (margin >= 70) return 'text-green-600';
    if (margin >= 60) return 'text-blue-600';
    if (margin >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCostRatioColor = (ratio: number) => {
    if (ratio <= 25) return 'bg-green-100 text-green-800';
    if (ratio <= 35) return 'bg-blue-100 text-blue-800';
    if (ratio <= 40) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ChefHat className="w-8 h-8" />
            レシピ管理システム
          </h1>
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('list')}
              className={`px-4 py-2 rounded ${activeTab === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            >
              メニュー一覧
            </button>
            <button
              onClick={() => setActiveTab('cost')}
              className={`px-4 py-2 rounded ${activeTab === 'cost' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            >
              原価分析
            </button>
            <button
              onClick={() => setActiveTab('possible')}
              className={`px-4 py-2 rounded ${activeTab === 'possible' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            >
              作成可能
            </button>
          </div>
        </div>

        {/* メニュー一覧タブ */}
        {activeTab === 'list' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* メニューリスト */}
            <div className="lg:col-span-1 bg-white rounded-lg shadow p-4">
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="メニュー検索..."
                  className="w-full px-3 py-2 border rounded"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {menus
                  .filter(m => m.name.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map(menu => (
                    <div
                      key={menu.id}
                      onClick={() => handleMenuSelect(menu)}
                      className={`p-3 border rounded cursor-pointer hover:bg-gray-50 ${
                        selectedMenu?.id === menu.id ? 'border-blue-500 bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold">{menu.name}</h3>
                          <p className="text-sm text-gray-600">{menu.category}</p>
                          <p className="text-lg font-bold">¥{menu.price.toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <span className={`text-sm font-semibold ${getProfitMarginColor(menu.profit_margin)}`}>
                            利益率 {menu.profit_margin}%
                          </span>
                          <p className="text-xs text-gray-500">
                            材料 {menu.ingredient_count}品
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* レシピ詳細 */}
            <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
              {recipeDetail ? (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">{recipeDetail.menu.name}のレシピ</h2>
                    {!editMode ? (
                      <button
                        onClick={() => setEditMode(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
                      >
                        <Edit className="w-4 h-4" />
                        編集
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveRecipe}
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2"
                        >
                          <Save className="w-4 h-4" />
                          保存
                        </button>
                        <button
                          onClick={() => {
                            setEditMode(false);
                            setEditingRecipe(recipeDetail.ingredients);
                          }}
                          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          キャンセル
                        </button>
                      </div>
                    )}
                  </div>

                  {/* 原価サマリー */}
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-sm text-gray-600">原価</p>
                      <p className="text-xl font-bold">¥{recipeDetail.summary.totalCost}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-sm text-gray-600">売価</p>
                      <p className="text-xl font-bold">¥{recipeDetail.summary.sellingPrice}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-sm text-gray-600">利益</p>
                      <p className="text-xl font-bold text-green-600">¥{recipeDetail.summary.profit}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-sm text-gray-600">利益率</p>
                      <p className={`text-xl font-bold ${getProfitMarginColor(recipeDetail.summary.profitMargin)}`}>
                        {recipeDetail.summary.profitMargin}%
                      </p>
                    </div>
                  </div>

                  {/* 材料リスト */}
                  <div className="space-y-2">
                    <h3 className="font-semibold mb-2">材料一覧</h3>
                    {editMode ? (
                      <>
                        {editingRecipe.map((ing, index) => (
                          <div key={index} className="flex items-center gap-2 p-2 border rounded">
                            <select
                              value={ing.ingredient_id}
                              onChange={(e) => handleIngredientChange(index, 'ingredient_id', e.target.value)}
                              className="flex-1 px-2 py-1 border rounded"
                            >
                              {availableIngredients.map(ai => (
                                <option key={ai.id} value={ai.id}>
                                  {ai.name} ({ai.category})
                                </option>
                              ))}
                            </select>
                            <input
                              type="number"
                              step="0.01"
                              value={ing.quantity}
                              onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
                              className="w-20 px-2 py-1 border rounded"
                              placeholder="数量"
                            />
                            <select
                              value={ing.unit || ''}
                              onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
                              className="w-20 px-2 py-1 border rounded"
                            >
                              <option value="">選択</option>
                              <option value="kg">kg</option>
                              <option value="g">g</option>
                              <option value="個">個</option>
                              <option value="L">L</option>
                              <option value="ml">ml</option>
                              <option value="本">本</option>
                              <option value="パック">パック</option>
                              <option value="枚">枚</option>
                            </select>
                            <div className="flex items-center gap-1">
                              <span className="text-sm text-gray-600">¥</span>
                              <input
                                type="number"
                                step="1"
                                value={ing.cost_per_unit || 0}
                                onChange={(e) => handleIngredientChange(index, 'cost_per_unit', e.target.value)}
                                className="w-20 px-2 py-1 border rounded"
                                placeholder="単価"
                              />
                              <span className="text-sm text-gray-600">/{ing.unit || '単位'}</span>
                            </div>
                            <span className="w-24 text-right font-semibold">
                              ¥{ing.ingredient_cost ? Math.round(ing.ingredient_cost) : 0}
                            </span>
                            <button
                              onClick={() => handleRemoveIngredient(index)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={handleAddIngredient}
                          className="w-full py-2 border-2 border-dashed border-gray-300 rounded hover:border-blue-400 flex items-center justify-center gap-2 text-gray-600"
                        >
                          <Plus className="w-4 h-4" />
                          材料を追加
                        </button>
                      </>
                    ) : (
                      recipeDetail.ingredients.map((ing, index) => (
                        <div key={index} className="flex items-center justify-between p-3 border rounded">
                          <div className="flex items-center gap-4">
                            <span className="font-medium">{ing.ingredient_name}</span>
                            <span className="text-gray-600">
                              {ing.quantity}{ing.unit || ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-500">
                              在庫: {ing.current_stock || 0}{ing.unit || ''}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              ing.stock_status === 'OK' ? 'bg-green-100 text-green-800' :
                              ing.stock_status === 'LOW' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {ing.stock_status === 'OK' ? '充足' :
                               ing.stock_status === 'LOW' ? '残少' : '不足'}
                            </span>
                            <span className="font-semibold">
                              ¥{ing.ingredient_cost ? Math.round(ing.ingredient_cost) : 0}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* 作成可能状態 */}
                  {!editMode && (
                    <div className={`mt-4 p-4 rounded ${
                      recipeDetail.summary.canMake ? 'bg-green-50' : 'bg-red-50'
                    }`}>
                      {recipeDetail.summary.canMake ? (
                        <div className="flex items-center gap-2 text-green-700">
                          <Check className="w-5 h-5" />
                          <span className="font-semibold">現在の在庫で作成可能です</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-red-700">
                          <X className="w-5 h-5" />
                          <span className="font-semibold">材料が不足しています</span>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  左側からメニューを選択してください
                </div>
              )}
            </div>
          </div>
        )}

        {/* 原価分析タブ */}
        {activeTab === 'cost' && costAnalysis && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-gray-50 p-4 rounded">
                <h3 className="font-semibold mb-2">平均原価率</h3>
                <p className="text-3xl font-bold">{costAnalysis.summary.averageCostRatio}%</p>
                <p className="text-sm text-gray-600">
                  {costAnalysis.summary.recipesRegistered}/{costAnalysis.summary.totalMenus} メニュー登録済み
                </p>
              </div>
              <div className="bg-red-50 p-4 rounded">
                <h3 className="font-semibold mb-2 text-red-700">高原価メニュー</h3>
                <p className="text-3xl font-bold text-red-600">{costAnalysis.highCost.length}</p>
                <p className="text-sm text-gray-600">原価率35%以上</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <h3 className="font-semibold mb-2 text-green-700">最適原価メニュー</h3>
                <p className="text-3xl font-bold text-green-600">{costAnalysis.optimal.length}</p>
                <p className="text-sm text-gray-600">原価率25-35%</p>
              </div>
            </div>

            <div className="space-y-4">
              {costAnalysis.highCost.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                    要改善メニュー（原価率35%以上）
                  </h3>
                  <div className="space-y-2">
                    {costAnalysis.highCost.map((menu: any) => (
                      <div key={menu.id} className="flex items-center justify-between p-3 border rounded">
                        <div>
                          <span className="font-medium">{menu.name}</span>
                          <span className="ml-2 text-sm text-gray-600">({menu.category})</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span>原価: ¥{Math.round(menu.cost)}</span>
                          <span>売価: ¥{menu.price}</span>
                          <span className={`px-2 py-1 rounded text-sm font-medium ${getCostRatioColor(menu.cost_ratio)}`}>
                            原価率 {menu.cost_ratio}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {costAnalysis.optimal.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    最適原価メニュー（25-35%）
                  </h3>
                  <div className="space-y-2">
                    {costAnalysis.optimal.map((menu: any) => (
                      <div key={menu.id} className="flex items-center justify-between p-3 border rounded">
                        <div>
                          <span className="font-medium">{menu.name}</span>
                          <span className="ml-2 text-sm text-gray-600">({menu.category})</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span>原価: ¥{Math.round(menu.cost)}</span>
                          <span>売価: ¥{menu.price}</span>
                          <span className={`px-2 py-1 rounded text-sm font-medium ${getCostRatioColor(menu.cost_ratio)}`}>
                            原価率 {menu.cost_ratio}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {costAnalysis.lowCost && costAnalysis.lowCost.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Check className="w-5 h-5 text-blue-600" />
                    低原価メニュー（25%未満）
                  </h3>
                  <div className="space-y-2">
                    {costAnalysis.lowCost.map((menu: any) => (
                      <div key={menu.id} className="flex items-center justify-between p-3 border rounded bg-blue-50">
                        <div>
                          <span className="font-medium">{menu.name}</span>
                          <span className="ml-2 text-sm text-gray-600">({menu.category})</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span>原価: ¥{Math.round(menu.cost)}</span>
                          <span>売価: ¥{menu.price}</span>
                          <span className={`px-2 py-1 rounded text-sm font-medium ${getCostRatioColor(menu.cost_ratio)}`}>
                            原価率 {menu.cost_ratio}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 作成可能タブ */}
        {activeTab === 'possible' && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-2">現在の在庫で作成可能なメニュー</h2>
              <p className="text-gray-600">材料の在庫状況から、現在作成できるメニューを表示します</p>
            </div>

            <div className="space-y-4">
              {menus.filter(m => m.ingredient_count > 0).length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-lg font-medium mb-2">レシピが登録されていません</p>
                  <p className="text-sm">メニュー一覧からレシピを登録してください</p>
                </div>
              ) : (
                <>
                  {menus.filter(m => m.ingredient_count > 0).map(menu => (
                    <div key={menu.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-lg">{menu.name}</h3>
                          <p className="text-sm text-gray-600">{menu.category}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">材料数</p>
                          <p className="text-lg font-semibold">{menu.ingredient_count}種類</p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <span className="text-sm">原価: ¥{Math.round(menu.total_cost)}</span>
                          <span className="text-sm">売価: ¥{menu.price}</span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getCostRatioColor((menu.total_cost / menu.price * 100))}`}>
                            原価率 {menu.profit_margin}%
                          </span>
                        </div>
                        <button
                          onClick={() => handleMenuSelect(menu)}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                        >
                          詳細を見る
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default RecipeManagement;